/**
 * Shared theme surface builders for gradients, masks, and layered fills.
 *
 * 1. Read colors from the supplied theme.
 * 2. Build CSS values that preserve the intended surface layers.
 * 3. Export each builder through the theme barrel when consumers need it.
 * Continues in `index.ts`, which exposes these builders to shared components.
 */
import { Theme, alpha } from '@mui/material/styles';

/** Subtle primary tint for AI-adjacent surfaces such as cards and timeline strips. */
export const getPrimaryTintSurface = (theme: Theme): string =>
  theme.palette.primary.tint ?? alpha(theme.palette.primary.main, 0.2);

/** Keeps a transparent status tint opaque against the default background when blocks overlap. */
export const getTintOverDefaultBackground = (theme: Theme, tint: string): string =>
  `linear-gradient(${tint}, ${tint}), ${theme.palette.background.default}`;

/** Accent fill for AI agent badges, chips, and buttons. */
export const getPrimaryAccentBackground = (theme: Theme): string =>
  `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`;

/** Text and icon color on primary accent backgrounds. */
export const getPrimaryAccentContrastText = (theme: Theme): string => theme.palette.primary.contrastText;

/** Horizontal primary tint gradient that fades into the paper background. */
export const getPrimaryTintFadeBackground = (theme: Theme, direction = '270deg'): string =>
  `linear-gradient(${direction}, ${getPrimaryTintSurface(theme)} 0%, ${alpha(theme.palette.background.paper, 0)} 100%)`;

/** Shimmer skeleton gradient for loading placeholders. */
export const getSkeletonShimmerBackground = (theme: Theme): string =>
  `linear-gradient(-45deg, ${theme.palette.action.hover} 40%, ${theme.palette.action.selected} 50%, ${theme.palette.action.hover} 60%)`;

/** Primary-tinted banner gradient over the paper background. */
export const getPrimaryTintPaperGradient = (theme: Theme): string =>
  `linear-gradient(to bottom, ${getPrimaryTintSurface(theme)}, ${theme.palette.background.paper})`;

/** Success hero gradient for celebration and confirmation surfaces. */
export const getSuccessHeroGradient = (theme: Theme): string =>
  `linear-gradient(102.57deg, ${theme.palette.success.main} 100%, ${theme.palette.success.dark} 100%)`;

/** Marketing hero gradient for auth and branded panels. */
export const getPrimaryHeroGradient = (theme: Theme): string =>
  `linear-gradient(102.57deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`;

/** Fade mask for truncated text over the current paper background. */
export const getPaperFadeMaskImage = (theme: Theme): string =>
  `linear-gradient(${theme.palette.background.paper}, ${alpha(theme.palette.background.paper, 0)})`;

/** Inline sparkle icon for agent mention chips rendered via CSS background-image. */
export const getAgentSparkleIconDataUri = (iconColor: string): string => {
  const encodedColor = encodeURIComponent(iconColor);
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 19 19' fill='none'%3E%3Cpath fill='${encodedColor}' fill-rule='evenodd' clip-rule='evenodd' d='M10.5 6.5C12.1569 6.5 13.5 5.15685 13.5 3.5C13.5 5.15685 14.8431 6.5 16.5 6.5C14.8431 6.5 13.5 7.84315 13.5 9.5C13.5 7.84315 12.1569 6.5 10.5 6.5ZM2.5 11.5C5.81371 11.5 8.5 8.81371 8.5 5.5C8.5 8.81371 11.1863 11.5 14.5 11.5C11.1863 11.5 8.5 14.1863 8.5 17.5C8.5 14.1863 5.81371 11.5 2.5 11.5Z'/%3E%3C/svg%3E`;
};

/** Shimmer text gradient for active AI agent status labels. */
export const getPrimaryAccentTextGradient = (theme: Theme): string =>
  `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`;
